import { busy, notBusy } from '../busy/actions.js'
import { handleAddErrors, handleGetErrors, handleUpdateErrors, handleDeleteErrors } from '../error/actions.js'
import { resetFileSearch } from '../files/actions.js'

import api from '../api.js'
import store from '../store.js'
import storage from '../storage.js'

export const DEPARTMENT_CREATE_ACTION = 'DEPARTMENT_CREATE_ACTION'
export const DEPARTMENT_SHOW_ADD_DIALOG_ACTION = 'DEPARTMENT_SHOW_ADD_DIALOG_ACTION'
export const DEPARTMENT_HIDE_ADD_DIALOG_ACTION = 'DEPARTMENT_HIDE_ADD_DIALOG_ACTION'
export const DEPARTMENT_CHANGE_ORDERING_ACTION = 'DEPARTMENT_CHANGE_ORDERING_ACTION'
export const DEPARTMENT_GET_OK_ACTION = 'DEPARTMENT_GET_OK_ACTION'
export const DEPARTMENT_UPDATE_ACTION = 'DEPARTMENT_UPDATE_ACTION'
export const DEPARTMENT_SHOW_EDIT_DIALOG_ACTION = 'DEPARTMENT_SHOW_EDIT_DIALOG_ACTION'
export const DEPARTMENT_HIDE_EDIT_DIALOG_ACTION = 'DEPARTMENT_HIDE_EDIT_DIALOG_ACTION'
export const DEPARTMENT_DELETE_ACTION = 'DEPARTMENT_DELETE_ACTION'
export const DEPARTMENT_SHOW_DELETE_DIALOG_ACTION = 'DEPARTMENT_SHOW_DELETE_DIALOG_ACTION'
export const DEPARTMENT_HIDE_DELETE_DIALOG_ACTION = 'DEPARTMENT_HIDE_DELETE_DIALOG_ACTION'

export const DEPARTMENT_GET_PAGE_ITEMS_ACTION = 'DEPARTMENT_GET_PAGE_ITEMS_ACTION'
export const DEPARTMENT_SET_ORDER_ACTION = 'DEPARTMENT_SET_ORDER_ACTION'
export const DEPARTMENT_SET_LIMIT_ACTION = 'DEPARTMENT_SET_LIMIT_ACTION'
export const DEPARTMENT_SET_OFFSET_ACTION = 'DEPARTMENT_SET_OFFSET_ACTION'
export const DEPARTMENT_SET_SEARCH_ACTION = 'DEPARTMENT_SET_SEARCH_ACTION'
export const DEPARTMENT_RESET_SEARCH_ACTION = 'DEPARTMENT_RESET_SEARCH_ACTION'
export const DEPARTMENT_SET_FILTER_QUERIES_ACTION = 'DEPARTMENT_SET_FILTER_QUERIES_ACTION'
export const DEPARTMENT_SET_SELECTED_ITEM_ID = 'DEPARTMENT_SET_SELECTED_ITEM_ID'

const TAG_FILTER_KEY = 'tags_string'
const OBJECT_TYPE_FILTER_KEY = 'object_type_name_string'
const TAG_FILTER_OP = '~'
const TAG_FILTER_MODE_OR = 'or'
const TAG_FILTER_MODE_AND = 'and'

function filterItemsByTagNames (items, selectedTagNames, matchMode = TAG_FILTER_MODE_OR) {
  if (!Array.isArray(selectedTagNames) || selectedTagNames.length === 0) {
    return items
  }

  const selected = new Set(
    selectedTagNames
      .filter((value) => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim().toLowerCase())
  )

  if (selected.size === 0) {
    return items
  }

  return items.filter((item) => {
    const tagNames = (item?.tags || [])
      .map((tag) => tag?.name)
      .filter((name) => typeof name === 'string' && name.trim().length > 0)
      .map((name) => name.trim().toLowerCase())

    if (matchMode === TAG_FILTER_MODE_AND) {
      return Array.from(selected).every((selectedTag) => tagNames.includes(selectedTag))
    }
    return tagNames.some((name) => selected.has(name))
  })
}

function filterItemsByAnyValues (items, selectedValues, getValue) {
  if (!Array.isArray(selectedValues) || selectedValues.length === 0) {
    return items
  }

  const selected = new Set(
    selectedValues
      .filter((value) => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim().toLowerCase())
  )

  if (selected.size === 0) {
    return items
  }

  return items.filter((item) => {
    const value = getValue(item)
    return typeof value === 'string' && selected.has(value.trim().toLowerCase())
  })
}

export function departmentCreateAction (department) {
  return {
    type: DEPARTMENT_CREATE_ACTION,
    department
  }
}

export function departmentUpdateAction (department) {
  return {
    type: DEPARTMENT_UPDATE_ACTION,
    department
  }
}

export function departmentDeleteAction (department) {
  return {
    type: DEPARTMENT_DELETE_ACTION,
    department
  }
}

export function setSelectedItemId (selectedItemId) {
  return function (dispatch) {
    dispatch({
      type: DEPARTMENT_SET_SELECTED_ITEM_ID,
      selectedItemId
    })
    dispatch(resetFileSearch('department'))
  }
}

/**
 * Add dialog
 */
export function showAddDepartmentDialog () {
  return {
    type: DEPARTMENT_SHOW_ADD_DIALOG_ACTION
  }
}

export function hideAddDepartmentDialog () {
  return {
    type: DEPARTMENT_HIDE_ADD_DIALOG_ACTION
  }
}

/**
 * Edit dialog
 */
export function showEditDepartmentDialog (department) {
  return {
    type: DEPARTMENT_SHOW_EDIT_DIALOG_ACTION,
    department
  }
}

export function hideEditDepartmentDialog () {
  return {
    type: DEPARTMENT_HIDE_EDIT_DIALOG_ACTION
  }
}

/**
 * Delete dialog
 */
export function showDeleteDepartmentDialog (department) {
  return {
    type: DEPARTMENT_SHOW_DELETE_DIALOG_ACTION,
    department
  }
}

export function hideDeleteDepartmentDialog () {
  return {
    type: DEPARTMENT_HIDE_DELETE_DIALOG_ACTION
  }
}

/**
 * CREATE
 */
export function addDepartment (department) {
  return function (dispatch) {
    dispatch(busy())
    api.addDepartment(department).then(() => {
      dispatch(notBusy())
    }).catch(handleAddErrors('department', dispatch))
  }
}

/**
 * READ
 */
export function getDepartments (params = {}) {
  return function (dispatch) {
    dispatch(busy())
    api.getDepartments(params).then(result => {
      dispatch(notBusy())
      dispatch(receivedDepartments(result.data.value))
    }).catch(handleGetErrors('department', dispatch))
  }
}

export function receivedDepartments (items) {
  return {
    type: DEPARTMENT_GET_OK_ACTION,
    items
  }
}

/**
 * UPDATE
 */
export function updateDepartment (department) {
  return function (dispatch) {
    dispatch(busy())
    api.updateDepartment(department).then(() => {
      dispatch(notBusy())
    }).catch(handleUpdateErrors('department', dispatch))
  }
}

/**
 * DELETE
 */
export function deleteDepartment (department) {
  return function (dispatch) {
    dispatch(busy())
    api.deleteDepartment(department).then(result => {
      dispatch(notBusy())
      dispatch(getPageItems())
    }).catch(handleDeleteErrors('department', dispatch))
  }
}

export function toggleFavorite (department) {
  return function (dispatch) {
    const favorite = !department.is_favorite
    dispatch(busy())
    api.setDepartmentFavorite(department.id, favorite).then(result => {
      dispatch(notBusy())
    }).catch(handleUpdateErrors('department', dispatch))
  }
}

/**
 * Get items for a page
 */
export function getPageItems () {
  const state = store.getState()
  const { search, filterQueries, limit, offset, orderBy, order } = state.department

  let query = []
  if (typeof search === 'string' && search.length > 0) {
    const split = search.split(',').map(s => s.trim()).filter(Boolean)
    if (split.length) {
      query.push({ key: 'name', value: split[0], op: '~' })
    }
  }

  const allFilters = Array.isArray(filterQueries) ? filterQueries : []
  const tagFilters = allFilters.filter((q) => q.key === TAG_FILTER_KEY && q.op === TAG_FILTER_OP)
  const objectTypeFilters = allFilters.filter((q) => q.key === OBJECT_TYPE_FILTER_KEY && q.op === TAG_FILTER_OP)
  const nonTagFilters = allFilters.filter((q) => {
    const isTagFilter = q.key === TAG_FILTER_KEY && q.op === TAG_FILTER_OP
    const isObjectTypeFilter = q.key === OBJECT_TYPE_FILTER_KEY && q.op === TAG_FILTER_OP
    return !isTagFilter && !isObjectTypeFilter
  })
  const selectedTagNames = tagFilters.map((q) => q.value)
  const selectedObjectTypeNames = objectTypeFilters.map((q) => q.value)
  const selectedTagMatchMode = tagFilters.find((q) => q.matchMode === TAG_FILTER_MODE_AND)
    ? TAG_FILTER_MODE_AND
    : TAG_FILTER_MODE_OR
  query = [...query, ...nonTagFilters]

  const sort = orderBy ? `${orderBy}.${order}` : null

  const numericLimit = Number(limit)
  const pageLimit = Number.isFinite(numericLimit) && numericLimit > 0 ? numericLimit : 50
  const numericOffset = Number(offset) || 0

  if (selectedTagNames.length > 0 || selectedObjectTypeNames.length > 0) {
    return function (dispatch) {
      dispatch(busy())
      api.getDepartments({
        query,
        sort,
        limit: Math.max(1000, numericOffset + pageLimit),
        offset: 0
      }).then(result => {
        let filteredItems = result.data.value
        filteredItems = filterItemsByTagNames(filteredItems, selectedTagNames, selectedTagMatchMode)
        filteredItems = filterItemsByAnyValues(filteredItems, selectedObjectTypeNames, (item) => item?.object_type_name)
        const pageItems = filteredItems.slice(numericOffset, numericOffset + pageLimit)
        dispatch(notBusy())
        dispatch(receivedDepartments(pageItems))
      }).catch(handleGetErrors('department', dispatch))
    }
  }

  return function (dispatch) {
    getDepartments({
      query,
      limit,
      offset,
      sort
    })(dispatch)
  }
}

/**
 * Sort order
 */
export function setOrder (orderBy, order) {
  return {
    type: DEPARTMENT_SET_ORDER_ACTION,
    orderBy,
    order
  }
}

/**
 * Set limit for pagination
 */
export function setLimit (limit) {
  return {
    type: DEPARTMENT_SET_LIMIT_ACTION,
    limit
  }
}

/**
 * Set offset for pagination
 */
export function setOffset (offset) {
  return {
    type: DEPARTMENT_SET_OFFSET_ACTION,
    offset
  }
}

/**
 * Set search
 */
export function setSearch (search) {
  return {
    type: DEPARTMENT_SET_SEARCH_ACTION,
    search
  }
}

/**
 * Set filter queries
 */
export function setFilterQueries (filterQueries) {
  storage.putPageSetting('department', { filterQueries })
  return {
    type: DEPARTMENT_SET_FILTER_QUERIES_ACTION,
    filterQueries
  }
}

/**
 * Reset search
 */
export function resetSearch () {
  return {
    type: DEPARTMENT_RESET_SEARCH_ACTION
  }
}
